import { storage } from '../../storage';
import { DomainHandler, HandlerContext } from '../../types';
import { ExportProgressData, ExportProgressStatus } from './export.types';
import { ExportType } from '../../../types/export.types';
import {
  buildAnalysisFileName,
  buildPoComponentsFileName,
  buildStockFileName,
  buildMaterialsDemandsFileName,
  downloadExportFile,
} from '../../../utils/download-utils';

function isValidMessage(msg: unknown): msg is { data: { status: string; [k: string]: unknown } } {
  if (typeof msg !== 'object' || msg === null) return false;
  return typeof (msg as any).data?.status === 'string';
}

function resolveFileName(data: ExportProgressData): string {
  switch (data.exportType) {
    case ExportType.EXPORT_ANALYSIS:
      return buildAnalysisFileName(parseInt(data.domainId, 10));
    case ExportType.EXPORT_PO_COMPONENTS:
      return buildPoComponentsFileName(data.hasFilters ?? false);
    case ExportType.EXPORT_STOCK:
      return buildStockFileName(data.hasFilters ?? false);
    default:
      return buildMaterialsDemandsFileName();
  }
}

function resolveDownloadType(
  t: ExportType,
): 'analysis' | 'po-components' | 'stock' | 'materials-demands' {
  if (t === ExportType.EXPORT_ANALYSIS) return 'analysis';
  if (t === ExportType.EXPORT_PO_COMPONENTS) return 'po-components';
  if (t === ExportType.EXPORT_STOCK) return 'stock';
  return 'materials-demands';
}

const REMOVAL_DELAY_MS = 1000;

export const exportHandler: DomainHandler = {
  onMessage(msg: unknown, _ctx: HandlerContext): boolean {
    if (!isValidMessage(msg)) return false;

    const { storageKey } = _ctx.descriptor;
    const status = msg.data.status as ExportProgressStatus;
    const d = msg.data as any;

    const prev = storage.get<ExportProgressData>(storageKey);
    if (!prev) return true; // storage limpo (F5 rápido) — fechar socket órfão

    const next: ExportProgressData = {
      ...prev,
      status,
      ...(d.started_at != null && { started_at: d.started_at }),
      ...(d.task_id != null && { task_id: d.task_id }),
      ...(d.file_url != null && { file_url: d.file_url }),
      ...(d.ended_at != null && { ended_at: d.ended_at }),
      ...(d.error != null && { error: d.error }),
    };

    storage.set(storageKey, next);

    if (status === 'FINISHED') {
      if (!d.file_url) {
        storage.set(storageKey, { ...next, status: 'ERROR', error: 'file_url ausente em FINISHED' });
        setTimeout(() => storage.remove(storageKey), REMOVAL_DELAY_MS);
        return true;
      }
      const fileName = resolveFileName(next);
      const downloadType = resolveDownloadType(next.exportType);
      downloadExportFile(d.file_url, { fileName }, downloadType)
        .then(() => setTimeout(() => storage.remove(storageKey), REMOVAL_DELAY_MS))
        .catch(() => {
          storage.set(storageKey, { ...next, status: 'ERROR', error: 'download falhou' });
          setTimeout(() => storage.remove(storageKey), REMOVAL_DELAY_MS);
        });
      return true;
    }

    if (status === 'ERROR') {
      setTimeout(() => storage.remove(storageKey), REMOVAL_DELAY_MS);
      return true;
    }

    return false;
  },
};
